// ============================================================================
// A warehouse work queue — "what does this warehouse owe right now".
//
// One component, two queues (`kind`): PICK legs waiting for the floor to pick
// them, and inbound legs whose goods have not been received yet. They are the
// same question asked of two ends of the building, so they are the same table.
//
// Three things drive the layout:
//   - WAREHOUSE first. A Bengaluru picker cannot act on a Hyderabad pick, so the
//     warehouse filter is the primary control, and it only offers locations that
//     actually have work.
//   - EXPRESS is a priority signal, not decoration: tagged on the row AND a
//     one-click filter, so "do the express ones first" is a single action.
//   - On the GRN queue, SOURCE says who is bringing the goods. A vendor-delivered
//     leg has no truck of ours to track, so chasing it means calling the vendor
//     against the date they promised — same tag-plus-filter treatment as express,
//     and the promised date rides in the tooltip.
//   - Ordered by SLA due date, oldest first — the order the desk should work it.
//     Overdue rows are called out rather than merely sorted.
//   - ESCALATION is the one action here: a stuck pick or an inbound that never landed is flagged to
//     the teams who can unstick it (same drawer, same log and same CRM deal as a booking's
//     escalation on the board — keyed on the STAGE, because a pick has no truck to key on). An
//     escalated row STAYS in the queue, unlike a truck on the board: the work is still owed, so the
//     tag and the "escalated only" cut are how it is tracked rather than a section of its own.
// ============================================================================
import { ExportOutlined, ReloadOutlined, ThunderboltFilled, WarningOutlined } from "@ant-design/icons";
import {
  Button,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { fmtIST, omsOrderPath } from "../../format";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchPendingQueue,
  listWarehouses,
  PendingFulfilment,
  PendingQueueKind,
  PendingStageRow,
  resolveStageIssue,
  WarehouseOption,
} from "../logistics.api";
import IssueDrawer, { IssueTarget } from "./issue_drawer";
import { EscalationTag } from "./board_meta";
import { BookingEscalation } from "../../types";
import UnloadingTag from "../../components/unloading_tag";

const COPY: Record<PendingQueueKind, { empty: string; work: string }> = {
  picklist: {
    empty: "Nothing to pick",
    work: "Legs issued to the floor and waiting to be picked.",
  },
  grn: {
    empty: "Nothing awaiting receipt",
    work: "Goods on their way in that have not been received against a GRN yet.",
  },
};

// Who is bringing the goods in. Only ever rendered on the GRN queue.
const SourceTag: React.FC<{ row: PendingStageRow }> = ({ row }) => {
  if (!row.fulfilment) return null;
  if (!row.is_supplier_delivered) {
    return (
      <Tooltip title="We collect from the vendor — a truck of ours carries this leg.">
        <Tag style={{ marginInlineEnd: 0 }}>we collect</Tag>
      </Tooltip>
    );
  }
  const promised = row.promised_availability_date
    ? fmtIST(row.promised_availability_date, "DD MMM")
    : null;
  const overdue = row.days_overdue ?? 0;
  return (
    <Tooltip
      title={
        overdue > 0
          ? `Vendor delivers this themselves. Promised ${promised} — ${overdue} day${
              overdue === 1 ? "" : "s"
            } overdue; follow up with the supplier.`
          : `Vendor delivers this themselves${promised ? ` — promised ${promised}` : ""}.`
      }
    >
      <Tag color={overdue > 0 ? "volcano" : "purple"} style={{ marginInlineEnd: 0 }}>
        supplier delivered{overdue > 0 ? ` · ${overdue}d late` : ""}
      </Tag>
    </Tooltip>
  );
};

// The allotment has a line-haul still ahead of it — landing at this dock is not the
// end of its journey. Worth calling out next to the source, because a late intercity
// inbound costs the customer twice: once here, once on the leg out.
const IntercityTag: React.FC<{ row: PendingStageRow }> = ({ row }) =>
  row.is_intercity ? (
    <Tooltip title="Intercity — these goods still have a line-haul to another city before they reach the customer.">
      <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
        intercity
      </Tag>
    </Tooltip>
  ) : null;

// Where the goods are headed after this warehouse is done with them. Only meaningful when the
// receiving dock and the fulfilment dock differ — i.e. a line-haul sits between them — and that is
// precisely the case a picker needs called out: they are pulling stock for a truck to ANOTHER city,
// not for a van to a customer down the road. Same-dock rows render nothing rather than a tag that
// would repeat the Warehouse column on every line.
const DestinationCell: React.FC<{ row: PendingStageRow }> = ({ row }) => {
  const { warehouse_id, fulfilment_warehouse_id, fulfilment_warehouse_name } = row;
  // Keyed on THIS row's own dock, not on the allocation: a row already sitting in the warehouse
  // that ships the last mile has nowhere further to send the goods, and tagging it would point an
  // arrow at the dock the picker is standing in.
  const movesOn =
    warehouse_id != null &&
    fulfilment_warehouse_id != null &&
    warehouse_id !== fulfilment_warehouse_id;
  if (!movesOn) return <span style={{ color: "#ccc" }}>—</span>;
  return (
    <Tooltip title={`Line-haul: these goods ship to the customer out of ${fulfilment_warehouse_name}, not from here.`}>
      <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
        → {fulfilment_warehouse_name}
      </Tag>
    </Tooltip>
  );
};

const DueCell: React.FC<{ row: PendingStageRow }> = ({ row }) => {
  if (!row.due_at) return <span style={{ color: "#ccc" }}>—</span>;
  const overdue = dayjs(row.due_at).isBefore(dayjs());
  return (
    <Tooltip title={`${fmtIST(row.due_at, "DD MMM YYYY, HH:mm")} IST`}>
      <span style={{ color: overdue ? "#cf1322" : undefined, fontWeight: overdue ? 600 : undefined }}>
        {fmtIST(row.due_at, "DD MMM")}
        {overdue ? " ⚠︎" : ""}
      </span>
    </Tooltip>
  );
};

const PendingQueuePanel: React.FC<{
  kind: PendingQueueKind;
  onCountChange?: (n: number) => void;
}> = ({ kind, onCountChange }) => {
  const [rows, setRows] = useState<PendingStageRow[]>([]);
  // Full MD warehouse list from /oms/logistics/warehouses/ — the same 4 MD warehouses that feed
  // the logistics board's filter. Used for BOTH the fulfilment and receiving dropdowns so the
  // desk can pick a warehouse even when the queue has no rows for it right now.
  const [mdWarehouses, setMdWarehouses] = useState<WarehouseOption[]>([]);
  const [fulfilmentWarehouseId, setFulfilmentWarehouseId] = useState<number | undefined>();
  const [receivingWarehouseId, setReceivingWarehouseId] = useState<number | undefined>();
  const [expressOnly, setExpressOnly] = useState(false);
  const [fulfilment, setFulfilment] = useState<PendingFulfilment | undefined>();
  const [intercityOnly, setIntercityOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [expressCount, setExpressCount] = useState(0);
  const [supplierDeliveredCount, setSupplierDeliveredCount] = useState(0);
  const [intercityCount, setIntercityCount] = useState(0);
  const [escalatedOnly, setEscalatedOnly] = useState(false);
  const [escalatedCount, setEscalatedCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Escalating a row — the desk's "somebody has to look at this pick / this inbound". The row does
  // NOT leave the queue when one is open (unlike a truck on the board, which moves into the
  // escalation section): the pick is still owed, so hiding it would lose the work.
  const [issueFor, setIssueFor] = useState<IssueTarget | null>(null);
  // Closing one out gets its own prompt rather than a bare confirm, because the close-out note goes
  // into the same operation log as the raise.
  const [resolveFor, setResolveFor] = useState<PendingStageRow | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    listWarehouses().then(setMdWarehouses);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await fetchPendingQueue({
      kind,
      fulfilment_warehouse_id: fulfilmentWarehouseId,
      receiving_warehouse_id: receivingWarehouseId,
      express: expressOnly,
      search,
      // Only the inbound queue has a source to filter on.
      fulfilment: kind === "grn" ? fulfilment : undefined,
      intercity: intercityOnly,
      escalated: escalatedOnly,
    });
    setLoading(false);
    if (!data) return;
    setRows(data.results ?? []);
    setExpressCount(data.express_count ?? 0);
    setSupplierDeliveredCount(data.supplier_delivered_count ?? 0);
    setIntercityCount(data.intercity_count ?? 0);
    setEscalatedCount(data.escalated_count ?? 0);
    onCountChange?.(data.count ?? 0);
  }, [
    
    kind,
   
    fulfilmentWarehouseId,
    receivingWarehouseId,
   
    expressOnly,
   
    fulfilment,
   
    intercityOnly,
    escalatedOnly,
   
    search,
   
    onCountChange,
  ,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  // Merge the escalation into the row in place rather than refetching: a reload would re-sort the
  // queue under the operator who just raised it, and the row they were working would move.
  // `hadOne` is the row's state BEFORE the change, so the badge count moves by the right amount:
  // re-raising an already-escalated row bumps its own `count`, it does not add a second row to the
  // filter's tally.
  const mergeEscalation = useCallback(
    (stageId: number, escalation: BookingEscalation | null, hadOne: boolean) => {
      setRows((prev) => prev.map((r) => (r.id === stageId ? { ...r, escalation } : r)));
      const delta = (escalation ? 1 : 0) - (hadOne ? 1 : 0);
      if (delta) setEscalatedCount((n) => Math.max(0, n + delta));
    },
    []
  );

  const runResolve = async () => {
    if (!resolveFor) return;
    setResolving(true);
    const ok = await resolveStageIssue(resolveFor.id, resolveNote);
    setResolving(false);
    if (ok) {
      mergeEscalation(resolveFor.id, null, true);
      setResolveFor(null);
    }
  };

  const columns = [
    {
      title: "Lead",
      key: "lead",
      width: 210,
      render: (_: unknown, r: PendingStageRow) => (
        <Space size={6} wrap>
          {r.is_express && (
            // The priority signal. Red + icon so it is legible at a glance down a long queue.
            <Tag color="red" icon={<ThunderboltFilled />} style={{ marginInlineEnd: 0 }}>
              express
            </Tag>
          )}
          <UnloadingTag show={r.is_unloading} />
          {/* An escalated row stays in the queue, so the tag is the only thing that says somebody
              has already flagged it — without it, two people chase the same stuck pick. */}
          <EscalationTag escalation={r.escalation} />
          {kind === "grn" && <SourceTag row={r} />}
          <IntercityTag row={r} />
          {r.sales_order_id ? (
            // A queue row IS one allotment, so the order opens on that card.
            <Link to={omsOrderPath(r.sales_order_id, { poNumber: r.po_number })}>
              {r.lead_id || `SO #${r.sales_order_id}`}
            </Link>
          ) : (
            <span>{r.lead_id || "—"}</span>
          )}
        </Space>
      ),
    },
    {
      title: "PO #",
      key: "po_number",
      width: 150,
      render: (_: unknown, r: PendingStageRow) =>
        r.po_number ? (
          <div>
            <div>{r.po_number}</div>
            {/* Cargo weight under the PO — same treatment as the First/Last Mile boards. */}
            {typeof r.cargo_weight_kg === "number" && r.cargo_weight_kg > 0 && (
              <div style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "nowrap" }}>
                {r.cargo_weight_kg} kg
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: "#ccc" }}>—</span>
        ),
    },
    { title: "Warehouse", dataIndex: "warehouse_name", width: 160 },
    {
      // Reads as "this dock → that dock" next to the Warehouse column, so a cross-city pick is
      // legible without opening the order.
      title: "Destination",
      key: "destination",
      width: 170,
      render: (_: unknown, r: PendingStageRow) => <DestinationCell row={r} />,
    },
    {
      title: kind === "grn" ? "Supplier" : "Customer",
      key: "party",
      width: 190,
      render: (_: unknown, r: PendingStageRow) =>
        (kind === "grn" ? r.supplier_name : r.client_name) || <span style={{ color: "#ccc" }}>—</span>,
    },
    {
      title: "Qty",
      key: "qty",
      width: 110,
      render: (_: unknown, r: PendingStageRow) => (
        <span>
          {r.qty} <Typography.Text type="secondary">({r.item_count} line)</Typography.Text>
        </span>
      ),
    },
    {
      title: "Due (SLA)",
      key: "due",
      width: 120,
      render: (_: unknown, r: PendingStageRow) => <DueCell row={r} />,
    },
    {
      // The document the row is actually about — the picklist the floor works off (or the
      // in-transit GRN on the inbound queue). Opens in Zoho BOOKS, the only Zoho app we link to.
      title: "Zoho",
      key: "zoho",
      width: 110,
      align: "center" as const,
      render: (_: unknown, r: PendingStageRow) =>
        r.zoho_url ? (
          <Tooltip title={r.zoho_number ? `Open ${r.zoho_number} in Zoho Books` : "Open in Zoho Books"}>
            <Button
              type="link"
              size="small"
              icon={<ExportOutlined />}
              href={r.zoho_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {r.zoho_number || ""}
            </Button>
          </Tooltip>
        ) : (
          <span style={{ color: "#ccc" }}>—</span>
        ),
    },
    {
      title: "State",
      key: "state",
      width: 130,
      render: (_: unknown, r: PendingStageRow) => (
        <Tooltip title={r.error_message || r.hold_reason || undefined}>
          <Tag color={r.status === "error" ? "error" : r.status === "held" ? "gold" : "processing"}>
            {String(r.status || r.issue_state).replace(/_/g, " ")}
          </Tag>
        </Tooltip>
      ),
    },
    {
      // The one action on this queue. It changes nothing about the order — it tells the teams who
      // can unstick the leg, and opens a CRM escalation on the order (see `IssueDrawer`).
      title: "Escalation",
      key: "escalation",
      width: 150,
      render: (_: unknown, r: PendingStageRow) =>
        r.escalation ? (
          <Space size={4}>
            <Button
              type="link"
              size="small"
              onClick={() => {
                setResolveNote("");
                setResolveFor(r);
              }}
            >
              Resolve
            </Button>
            <Tooltip title="Flag it again — a second raise re-notifies the teams and bumps the count on the row.">
              <Button
                type="link"
                size="small"
                danger
                onClick={() => setIssueFor({ kind: "stage", row: r, queue: kind })}
              >
                Re-raise
              </Button>
            </Tooltip>
          </Space>
        ) : (
          <Button
            type="link"
            size="small"
            danger
            icon={<WarningOutlined />}
            onClick={() => setIssueFor({ kind: "stage", row: r, queue: kind })}
          >
            Escalate
          </Button>
        ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }} size={8}>
        <Select
          style={{ width: 220 }}
          size="small"
          allowClear
          placeholder="All fulfilment WHs"
          value={fulfilmentWarehouseId}
          onChange={setFulfilmentWarehouseId}
          options={mdWarehouses.map((w) => ({
            value: w.id,
            label: w.city ? `${w.label} · ${w.city}` : w.label,
          }))}
        />
        <Select
          style={{ width: 220 }}
          size="small"
          allowClear
          placeholder="All receiving WHs"
          value={receivingWarehouseId}
          onChange={setReceivingWarehouseId}
          options={mdWarehouses.map((w) => ({
            value: w.id,
            label: w.city ? `${w.label} · ${w.city}` : w.label,
          }))}
        />
        <Input.Search
          style={{ width: 240 }}
          size="small"
          allowClear
          placeholder="Lead id, PO #, client…"
          onSearch={setSearch}
        />
        <Space size={6}>
          <Switch size="small" checked={expressOnly} onChange={setExpressOnly} />
          <span style={{ fontSize: 12 }}>
            Express only{expressCount ? ` (${expressCount})` : ""}
          </span>
        </Space>
        <Space size={6}>
          {/* The other cut that changes how a row is worked: an intercity allotment has a
              line-haul still ahead of it, so a slip here costs the customer twice. */}
          <Switch size="small" checked={intercityOnly} onChange={setIntercityOnly} />
          <span style={{ fontSize: 12 }}>
            Intercity only{intercityCount && !intercityOnly ? ` (${intercityCount})` : ""}
          </span>
        </Space>
        <Space size={6}>
          {/* What has already been flagged — the rows somebody is chasing, so the desk can work
              (or hand over) just those without reading the whole queue. */}
          <Switch size="small" checked={escalatedOnly} onChange={setEscalatedOnly} />
          <span style={{ fontSize: 12 }}>
            Escalated only{escalatedCount && !escalatedOnly ? ` (${escalatedCount})` : ""}
          </span>
        </Space>
        {kind === "grn" && (
          // The follow-up cut: vendor-delivered legs are chased with the SUPPLIER, not
          // tracked on a truck, so the desk can narrow to just those without leaving the tab.
          <Select
            style={{ width: 210 }}
            size="small"
            allowClear
            placeholder="Any source"
            value={fulfilment}
            onChange={(v) => setFulfilment(v as PendingFulfilment | undefined)}
            options={[
              {
                value: "supplier_delivered",
                label: `Supplier delivered${
                  supplierDeliveredCount ? ` (${supplierDeliveredCount})` : ""
                }`,
              },
              { value: "self_pickup", label: "We collect" },
            ]}
          />
        )}
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {COPY[kind].work}
        </Typography.Text>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
        columns={columns}
        dataSource={rows}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: <Empty description={COPY[kind].empty} /> }}
        // Express rows tint the whole row, so priority survives a quick scroll past the tag column.
        rowClassName={(r) => (r.is_express ? "oms-row-express" : "")}
      />

      <IssueDrawer
        target={issueFor}
        onClose={() => setIssueFor(null)}
        onRaised={(escalation) => {
          if (issueFor?.kind !== "stage") return;
          // A raise that answered without the row's escalation (a 502 whose body carried none) is
          // still a raise — reload rather than merge a null, or the row would sit here looking
          // un-escalated and the next operator would flag it a second time.
          if (escalation) mergeEscalation(issueFor.row.id, escalation, !!issueFor.row.escalation);
          else load();
        }}
      />

      <Modal
        title={`Resolve escalation · ${resolveFor?.lead_id || resolveFor?.po_number || ""}`}
        open={!!resolveFor}
        onCancel={() => setResolveFor(null)}
        onOk={runResolve}
        okText="Resolve escalation"
        confirmLoading={resolving}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          {resolveFor?.escalation?.issue_type}
          {resolveFor?.escalation?.remarks ? ` — “${resolveFor.escalation.remarks}”` : ""}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          This clears the flag on the row. The work itself is unaffected — it stays in this queue
          until the pick or the receipt is actually done — and the escalation stays in the operation
          log; nothing is erased.
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          maxLength={500}
          showCount
          value={resolveNote}
          onChange={(e) => setResolveNote(e.target.value)}
          placeholder="What closed it out? (optional)"
        />
      </Modal>
    </div>
  );
};

export default PendingQueuePanel;
